import {fadeInAnimation} from '../../animations/fade-in.animation';
import {Logger} from '../../class/helper/logger.class';
import {MessageAlertService} from '../../services/message-alert/message-alert.service';
import {IPaginatorConfiguration} from '../paginator/paginator.component';
import {UtilitiesService} from '../../services/utilities-service/utilities.service';
import {Loader} from '../../models/loader/loader.redux';
import {NoteListDTO} from '../../dtos/service';
import {firstValueFrom, Observable} from 'rxjs';
import {INoteTimestamp, ServiceAction, ServiceState} from '../../models/service';
import {Store} from '@ngxs/store';
import {StorageService} from '../../services/storage/storage.service';
import {IUser} from '../../models/auth';
import {AfterViewInit, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {RolesEnum} from '../../enums/role';
import {UserService} from '../../services/user/user.service';

@Component({
  selector: 'app-view-notes',
  templateUrl: './view-notes.component.html',
  styleUrls: ['./view-notes.component.scss'],
  animations: [fadeInAnimation]
})
export class ViewNotesComponent implements OnInit, AfterViewInit {
  @Input() clinicId: string = null;
  @Input() patientId: string = null;
  @Input() serviceId: string = null;
  @Input() isActiveService: boolean;
  @Input() appointmentId: string = null;
  @Input() allAppointments: boolean;
  @Input() allowToAssign: boolean;
  @Output() addNote: EventEmitter<{ appointmentId: string, isActiveService: boolean }> = new EventEmitter();
  noteSelected: INoteTimestamp;
  noteSelectedId: string;
  actualInterval: any; // Intervalo para buscar despues de N tiempo que se presiona una tecla
  search: string; // string del campo a buscar
  searchNotes: string;
  filterNotes: string;
  searchNotesTemporal: string;
  filterNotesTemporal: string;
  userLogged: IUser;
  loadNotes: boolean;
  currentNotesTab = 'BY_SERVICE';
  indexPageService: number;
  indexPage: number;
  paginatorConfig: IPaginatorConfiguration;
  isClosed: boolean;

  notesByService$: Observable<NoteListDTO[]> = this.store.select<NoteListDTO[]>(ServiceState.getNotesByService);
  total$: Observable<number> = this.store.select<number>(ServiceState.getNotesCount);
  totalNotesByService$: Observable<number> = this.store.select<number>(ServiceState.getNotesByServiceCount);
  notes$: Observable<NoteListDTO[]> = this.store.select<NoteListDTO[]>(ServiceState.getNotes);
  roleAuthUser: RolesEnum | undefined;
  roles = RolesEnum;

  constructor(
    private store: Store,
    private storageService: StorageService,
    private utilitiesService: UtilitiesService,
    private messageAlertService: MessageAlertService,
    private userService: UserService,
  ) {
  }

  ngOnInit(): void {
    this.init();
  }

  /**
   * Inicializa las propiedades necesarias.
   */
  init(): void {
    this.userLogged = this.storageService.getLocalItem<{ token: string, user: IUser }>('user')?.user;
    this.searchNotes = '';
    this.search = '';
    this.paginatorConfig = {
      nzPageSize: 10
    };
    this.indexPage = 1;
    this.indexPageService = 1;
    this.roleAuthUser = this.userService.getRoleByUser();
  }

  async ngAfterViewInit(): Promise<void> {
    try {
      await firstValueFrom((this.store.dispatch(new Loader(true, 'Cargando notas ...'))));
      await this.getDataByService(true);
    } catch (e) {
      Logger.log(e);
    } finally {
      await firstValueFrom((this.store.dispatch(new Loader(false))));
    }
  }

  onEmitAddNote(): void {
    this.addNote.emit({
      appointmentId: this.appointmentId,
      isActiveService: this.isActiveService
    });
  }

  /**
   * método para buscar notas cada que se escribe algo en el campo de búsqueda
   * @param $event cadena de texto para la busqueda
   */
  async onSearch($event): Promise<void> {
    if (this.actualInterval !== null) {
      clearInterval(this.actualInterval);
    }
    this.actualInterval = setTimeout(async () => {
      if (this.search === $event.target.value) return;
      this.search = $event.target.value;
      await firstValueFrom((this.store.dispatch(new Loader(true))));
      if (this.currentNotesTab === 'BY_SERVICE') {
        // notas por servicio
        this.indexPageService = 1;
        await this.getDataByService(true);
      } else {
        // notas por cita
        this.indexPage = 1;
        await this.getData(true);
      }
      await firstValueFrom((this.store.dispatch(new Loader(false))));
    }, 400);
  }

  /**
   * método que envia la petición para obtener el número total de notas por servicio y la lista de notas paginadas
   * @param withCount indica si se hará la petición de traer el total de notas
   */
  async getDataByService(withCount: boolean = false): Promise<void> {
    if (withCount) {
      await this.getCountNotesByService();
    }
    const showNotes = (this.filterNotes === 'shared') ? 'shared' : 'all';
    const filterData = this.utilitiesService.filterCountPagination(this.indexPageService, this.paginatorConfig.nzPageSize);
    if (this.serviceId) {
      await firstValueFrom(this.store.dispatch(new ServiceAction.GetNotes
      (this.serviceId, this.patientId, this.search, showNotes, filterData)));
    }
    this.loadNotes = true;
  }

  /**
   * método que envia la petición para obtener el número total de notas y la lista de notas paginadas
   * @param withCount indica si se hará la petición de traer el total de notas
   */
  async getData(withCount: boolean = false): Promise<void> {
    if (withCount) {
      await this.getCountNotes();
    }
    const showNotes = (this.filterNotes === 'shared') ? 'shared' : 'all';
    const filterData = this.utilitiesService.filterCountPagination(this.indexPage, this.paginatorConfig.nzPageSize);
    if (this.allAppointments) {
      await firstValueFrom(this.store.dispatch(new ServiceAction.GetNotesAllAppointments(
        this.serviceId,
        this.patientId,
        this.search,
        showNotes,
        filterData
      )));
    } else {
      await firstValueFrom(this.store.dispatch(new ServiceAction.GetAppointmentNotes
      (this.appointmentId, this.patientId, this.search, showNotes, filterData)));
    }
  }

  /**
   * método que hace la peticion para obtener el total de notas por servicio
   */
  async getCountNotesByService(): Promise<void> {
    const showNotes = (this.filterNotes === 'shared') ? 'shared' : 'all';
    const filterData = this.utilitiesService.filterCountPagination(this.indexPage, this.paginatorConfig.nzPageSize);
    if (this.serviceId) {
      await firstValueFrom(this.store.dispatch(new ServiceAction.CountNotes(this.serviceId,
        this.patientId, this.search, showNotes, filterData)));
    }
  }

  /**
   * método que hace la peticion para obtener el total de notas
   */
  async getCountNotes(): Promise<void> {
    const showNotes = (this.filterNotes === 'shared') ? 'shared' : 'all';
    const filterData = this.utilitiesService.filterCountPagination(this.indexPage, this.paginatorConfig.nzPageSize);
    // obtener el id de la consulta seleccionada para abrir el modal primero
    if (this.allAppointments) {
      await firstValueFrom(this.store.dispatch(new ServiceAction.CountNotesAllAppointments(
        this.serviceId,
        this.patientId,
        this.search,
        showNotes,
        filterData
      )));
    } else {
      await firstValueFrom(this.store.dispatch(new ServiceAction.CountAppointmentNotes
      (this.appointmentId, this.patientId, this.search, showNotes, filterData)));
    }
  }

  /**
   * event when select one note
   */
  onSelectedNote(event: INoteTimestamp): void {
    this.noteSelected = event;
    this.noteSelectedId = event._id;
  }

  /**
   * método para buscar notas cada que se cambia el select de notas a mostrar
   */
  async onChangeSelect(showLoader: boolean = true): Promise<void> {
    this.search = this.searchNotes;
    if (showLoader) {
      await firstValueFrom((this.store.dispatch(new Loader(true))));
    }
    if (this.currentNotesTab === 'BY_SERVICE') {
      // notas por servicio
      this.indexPageService = 1;
      await this.getDataByService(true);

    } else {
      // notas por cita
      this.indexPage = 1;
      await this.getData(true);
    }
    if (showLoader) {
      await firstValueFrom((this.store.dispatch(new Loader(false))));
    }
  }

  async onChangeSharedNote(
    noteToShare: { noteId: string, isShared: boolean },
    type: 'appointmentNotes' | 'serviceNotes',
  ): Promise<void> {
    try {
      await firstValueFrom((this.store.dispatch(new Loader(true))));
      await firstValueFrom((this.store.dispatch(new ServiceAction.PatchAppointmentNote(noteToShare.noteId, noteToShare.isShared, type))));
      const showNotes = (this.filterNotes === 'shared') ? 'shared' : 'all';
      if (showNotes === 'shared') {
        await this.onChangeSelect(false);
      }
      const message = noteToShare.isShared ? 'Se ha compartido la nota' : 'Se ha dejado de compartir la nota';
      this.messageAlertService.createMessage('success', message);
    } catch (e) {
      Logger.log('Ocurrio un error', e?.message);
      this.messageAlertService.createMessage('error', this.messageAlertService.getMessageError(e?.message));
    } finally {
      await firstValueFrom((this.store.dispatch(new Loader(false))));
    }
  }

  async onDeleteNote(noteToDelete: { noteId: string, id: string }): Promise<void> {
    await firstValueFrom((this.store.dispatch(new Loader(true))));
    try {
      await firstValueFrom((this.store.dispatch(new ServiceAction.DeleteAppointmentNoteById(noteToDelete.id))));
      await firstValueFrom((this.store.dispatch(new ServiceAction.GetAppointmentsByService(this.patientId, this.userLogged.experts[0]._id))));
      await this.onChangeSelect(false);
      const message = 'Se ha eliminado la nota';
      this.messageAlertService.createMessage('success', message);
    } catch (e) {
      Logger.log('Ocurrió un error', e?.message);
      this.messageAlertService.createMessage('error', this.messageAlertService.getMessageError(e?.message));
    } finally {
      await firstValueFrom((this.store.dispatch(new Loader(false))));
    }
  }

  /**
   * método para obtener notas por servicio mediante la paginación
   * @param index número de la siguiente página
   */
  async onChangePaginationByService(index: number): Promise<void> {
    this.loadNotes = false;
    await firstValueFrom((this.store.dispatch(new Loader(true, 'Obteniendo las notas...'))));
    this.indexPageService = index;
    await this.getDataByService();
    this.loadNotes = true;
    await firstValueFrom((this.store.dispatch(new Loader(false))));
  }

  /**
   * método para obtener notas mediante la paginación
   * @param index número de la siguiente página
   */
  async onChangePagination(index: number): Promise<void> {
    this.loadNotes = false;
    await firstValueFrom((this.store.dispatch(new Loader(true, 'Obteniendo las notas ...'))));
    this.indexPage = index;
    await this.getData();
    this.loadNotes = true;
    await firstValueFrom((this.store.dispatch(new Loader(false))));
  }

  /**
   * método que hace la peticion para obtener las notas al cambiar el tab a 'Notas del servicio' o 'Notas de la cita'
   */
  async onChangeTabNotes(event): Promise<void> {
    if (this.isClosed) return;
    this.currentNotesTab = (event === 0) ? 'BY_SERVICE' : 'BY_APPOINTMENT';
    await firstValueFrom((this.store.dispatch(new Loader(true))));
    // detectar si es la pestaña de 'notas de la cita' o 'notas del servicio'
    if ((this.filterNotesTemporal !== this.filterNotes) || (this.searchNotesTemporal !== this.search)) {
      if (this.currentNotesTab === 'BY_SERVICE') {
        this.indexPageService = 1;
        await this.getDataByService(true);
      } else {
        // notas por cita
        this.indexPage = 1;
        await this.getData(true);
      }
    }
    this.filterNotesTemporal = this.filterNotes;
    this.searchNotesTemporal = this.search;
    await firstValueFrom((this.store.dispatch(new Loader(false))));
  }

  reset(): void {
    this.filterNotes = '';
    this.search = '';
    this.searchNotes = '';
    this.filterNotesTemporal = 'null';
    this.searchNotesTemporal = 'null';
    this.indexPage = 1;
    this.indexPageService = 1;
    this.currentNotesTab = 'BY_SERVICE';
    this.noteSelectedId = '';
  }
}
